import React, { useCallback, useState, useEffect } from 'react';
import flowRight from 'lodash.flowright';
import { withStyles } from '@material-ui/styles';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Checkbox,
  CircularProgress
} from '@material-ui/core';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom/cjs/react-router-dom.min';

import styles from './styles';
import { translate } from '../../i18n/I18nProvider';
import sendActivationMessage from './actions/sendActivationMessage';
import ActivationResultDialog from './ActivationResultDialog';
import DraggableDialog from '../DraggableDialog';
import PrimaryButton from '../customButtons/PrimaryButton';
import SecondaryButton from '../customButtons/SecondaryButton';
import CustomDialogTitle from '../customDialogTitle/CustomDialogTitle';
import { actions as reduxActions } from './reducers';
import getUserInfoFromLocation from '../../pages/user/actions/getUserInfoFromLocation';
import { doesArrayHasLength, isDefined } from '../../utils/isDefined';
import Optional from '../../utils/optional';
import getUser from './actions/getUser';
import getDisabledFields from './actions/getDisabledFields';

const DEFAULT_STATE = {
  sendForWeb: false,
  sendForMobile: false,
  sendForSoftphone: false,
  sendForWebSoftphone: false,
  sendForCrm: false,
  sendForTablet: false,
  sendEmail: false,
  sendSms: false
};

const ActivationMessageDialog = ({
  classes,
  clientDomain,
  users = [],
  open,
  closeDialog,
  location,
  currentUserDomain,
  fromuserProfile,
  isBulkRequest
}) => {
  const [data, setData] = useState(DEFAULT_STATE);
  const [errors, setErrors] = useState(void 0);
  const [results, setResults] = useState(void 0);
  const [isLoading, setIsLoading] = useState(false);
  const { isCurrentUser } = getUserInfoFromLocation({ location });
  const username = users[0]?.username;
  const disabledFields = getDisabledFields({
    user: users[0],
    fromuserProfile,
    isBulkRequest
  });

  useEffect(() => {
    let isCancelled = false;

    const fetchUser = async () => {
      setIsLoading(true);
      try {
        await getUser({
          userId: username,
          clientConnectId: clientDomain
        });
      } catch (e) {
        console.error(e);
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    if (!fromuserProfile && !isBulkRequest && open) {
      fetchUser();
    }

    return () => {
      isCancelled = true;
    };
  }, [open, fromuserProfile, isBulkRequest, username, clientDomain]);

  const onCloseDialog = () => {
    setData(DEFAULT_STATE);
    setErrors(void 0);
    setResults(void 0);
    closeDialog();
  };

  const closeFormDialog = () => {
    setData(DEFAULT_STATE);
    closeDialog();
  };

  const onCloseResultDialog = () => {
    setErrors(void 0);
    setResults(void 0);
  };

  const handleConfirm = useCallback(
    async () => {
      setErrors(void 0);
      setResults(void 0);
      const {
        isActionCompleted,
        responseErrors,
        results: activationResults
      } = await sendActivationMessage({
        clientDomain: isCurrentUser ? currentUserDomain : clientDomain,
        usersConnectIds: users.map(user =>
          Optional(user?.user_id).or(
            Optional(user?.connect30_user_id).or(user?.username)
          )
        ),
        data
      });

      if (users.length > 1 && doesArrayHasLength(activationResults)) {
        setResults(activationResults);
        closeFormDialog();
      } else if (isActionCompleted) {
        onCloseDialog();
      } else {
        setErrors(responseErrors);
      }
    },
    // eslint-disable-next-line
    [currentUserDomain, clientDomain, users, isCurrentUser, data]
  );

  const handleChange = (e, key) => {
    const changedData = { ...data, [key]: e.target.checked };

    if (
      ['sendForWeb', 'sendForSoftphone', 'sendForWebSoftphone'].includes(key)
    ) {
      if (e.target.checked) {
        changedData.sendEmail = true;
      } else {
        if (
          !changedData.sendForWeb &&
          !changedData.sendForSoftphone &&
          !changedData.sendForWebSoftphone
        ) {
          changedData.sendEmail = false;
        }
      }
    }

    if (key === 'sendForMobile') {
      changedData.sendSms = e.target.checked;
    }

    setData(changedData);
  };

  return (
    <>
      <Dialog
        maxWidth="lg"
        classes={{
          paper: classes.root
        }}
        PaperComponent={DraggableDialog}
        open={open}
        onClose={(event, reason) => {
          if (reason !== 'backdropClick') {
            onCloseDialog();
          }
        }}
      >
        <CustomDialogTitle onCloseClick={onCloseDialog}>
          {translate('TOOLTIP.ACTIVATION_MESSAGE')}
        </CustomDialogTitle>
        <DialogContent className={classes.dialogContent}>
          {isLoading && (
            <div className={classes.loaderOverlay}>
              <CircularProgress />
            </div>
          )}
          <div className={classes.info}>
            {translate('USER.ACTIVATION_MESSAGE_INFO_1')}
          </div>
          <div className={classes.row}>
            <Checkbox
              checked={data.sendForWeb}
              onChange={e => handleChange(e, 'sendForWeb')}
            />
            <div>Web</div>
          </div>
          <div className={classes.row}>
            <Checkbox
              checked={data.sendForMobile}
              onChange={e => handleChange(e, 'sendForMobile')}
              disabled={disabledFields.sendForMobile}
            />
            <div>Mobile</div>
          </div>

          <div className={classes.row}>
            <Checkbox
              checked={data.sendForSoftphone}
              onChange={e => handleChange(e, 'sendForSoftphone')}
              disabled={disabledFields.sendForSoftphone}
            />
            <div>Softphone</div>
          </div>
          <div className={classes.row}>
            <Checkbox
              checked={data.sendForWebSoftphone}
              onChange={e => handleChange(e, 'sendForWebSoftphone')}
              disabled={disabledFields.sendForWebSoftphone}
            />
            <div>WebSoftphone</div>
          </div>
          <div className={classes.info}>
            {translate('USER.ACTIVATION_MESSAGE_INFO_2')}
          </div>
          <div className={classes.row}>
            <Checkbox
              checked={data.sendEmail}
              onChange={e => handleChange(e, 'sendEmail')}
            />
            <div>E-Mail</div>
          </div>
          <div className={classes.row}>
            <Checkbox
              checked={data.sendSms}
              onChange={e => handleChange(e, 'sendSms')}
              disabled={disabledFields.sendForMobile}
            />
            <div>Sms</div>
          </div>
          <div className={classes.smallInfo}>
            {translate('USER.ACTIVATION_MESSAGE_INFO_3')}{' '}
          </div>
        </DialogContent>
        <DialogActions className={classes.footer}>
          <SecondaryButton onClick={onCloseDialog} disabled={isLoading}>
            {translate('BUTTON.CANCEL')}
          </SecondaryButton>
          <PrimaryButton
            onClick={handleConfirm}
            disabled={
              isLoading ||
              (isFalse(data.sendEmail) && isFalse(data.sendSms)) ||
              (isFalse(data.sendForWeb) &&
                isFalse(data.sendForMobile) &&
                isFalse(data.sendForSoftphone) &&
                isFalse(data.sendForWebSoftphone))
            }
          >
            OK
          </PrimaryButton>
        </DialogActions>
      </Dialog>
      <ActivationResultDialog
        open={isDefined(results) || isDefined(errors)}
        onClose={onCloseResultDialog}
        results={results}
        errors={errors}
      />
    </>
  );
};

const mapStatesToProps = ({ selectClient, activationMessageDialog, auth }) => {
  return {
    ...activationMessageDialog,
    clientDomain: selectClient.selectedClient?.connect30_domain,
    currentUserDomain: auth?.defaultClient?.connect30_domain
  };
};

const mapDispatchToProps = {
  closeDialog: reduxActions.closeDialog
};

export default flowRight(
  withRouter,
  connect(mapStatesToProps, mapDispatchToProps),
  withStyles(styles)
)(ActivationMessageDialog);

const isFalse = key => {
  return key === false;
};
